/**
 * Shared color helpers for all WorkWell Finance Dashboard charts (ECharts via ngx-echarts).
 *
 * Hard rule: no chart colors are fixed in TypeScript. Every color is read at
 * runtime from the design-system CSS custom properties defined in styles.scss.
 * Because the tokens swap on `html.dark`, charts rebuild their ECharts option
 * object on a ThemeService.isDark$ change and these helpers re-read the new
 * token values.
 */

/** Reads a CSS custom property off :root at runtime (trimmed). */
export function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * Named accessor for the design-system colors charts use, as plain CSS color
 * strings (what ECharts expects). Re-read on every call so a theme switch is
 * always reflected.
 */
export function chartColors() {
  return {
    primary:        cssVar('--color-primary'),
    primaryBg:      cssVar('--color-primary-bg'),
    accent:         cssVar('--color-accent'),
    accentBg:       cssVar('--color-accent-bg'),
    chartSecondary: cssVar('--color-chart-secondary'),
    chartSeries1:    cssVar('--color-chart-series-1'),
    chartSeries2:    cssVar('--color-chart-series-2'),
    chartSeries3:    cssVar('--color-chart-series-3'),
    chartSeries4:    cssVar('--color-chart-series-4'),
    chartSeries5:    cssVar('--color-chart-series-5'),
    chartSeries6:    cssVar('--color-chart-series-6'),
    chartSeries7:    cssVar('--color-chart-series-7'),
    chartSeries8:    cssVar('--color-chart-series-8'),
    chartSeries9:    cssVar('--color-chart-series-9'),
    chartSeries10:   cssVar('--color-chart-series-10'),
    positive:       cssVar('--color-positive'),
    positiveBg:     cssVar('--color-positive-bg'),
    negative:       cssVar('--color-negative'),
    negativeBg:     cssVar('--color-negative-bg'),
    warning:        cssVar('--color-warning'),
    warningBg:      cssVar('--color-warning-bg'),
    textPrimary:    cssVar('--color-text-primary'),
    textSecondary:  cssVar('--color-text-secondary'),
    muted:          cssVar('--color-text-muted'),
    cardBorder:     cssVar('--color-chart-grid') || cssVar('--color-card-border'),
    cardBg:         cssVar('--color-card-bg'),
    chartSurface:   cssVar('--color-chart-surface'),
  };
}

/**
 * Finance chart palette, constrained to the Graphite Blue theme accents.
 * Semantic status colors are deliberately not used for non-status slices.
 */
export function chartPalette(): string[] {
  const c = chartColors();
  return [
    c.chartSeries1 || c.primary,
    c.chartSeries2 || c.accent,
    c.chartSeries3 || c.warning,
    c.chartSeries4 || c.negative,
    c.chartSeries5 || c.chartSecondary,
    c.chartSeries6 || c.textSecondary,
    c.chartSeries7 || c.positive,
    c.chartSeries8 || c.muted,
    c.chartSeries9 || c.cardBorder,
    c.chartSeries10 || c.primary,
  ].filter(Boolean);
}

const KPI_COLOR_VAR_BY_KEY: Record<string, string> = {
  Revenue: '--color-positive',
  revenue: '--color-positive',
  NFI: '--kpi-color-nfi',
  nfi: '--kpi-color-nfi',
  gross_profit_margin: '--kpi-color-gross-profit-margin',
  ebitda: '--kpi-color-ebitda-margin',
  ebitda_margin: '--kpi-color-ebitda-margin',
  net_profit: '--kpi-color-net-profit-margin',
  net_profit_margin: '--kpi-color-net-profit-margin',
  debtor_days: '--kpi-color-debtor-days',
  cash_collected_pct: '--kpi-color-cash-collected-pct',
  aged_debt_90_pct: '--kpi-color-aged-debt-90-pct',
};

const KPI_FALLBACK_COLORS = [
  '#2457D6',
  '#1AAE9F',
  '#6D5BD0',
  '#F59E0B',
  '#EF4444',
  '#129E91',
  '#F97316',
] as const;

function kpiColorVarName(key: string | null | undefined, dark = false): string | null {
  if (!key) return null;
  const varName = KPI_COLOR_VAR_BY_KEY[key];
  return varName ? `${varName}${dark ? '-dark' : ''}` : null;
}

export function kpiColor(key: string | null | undefined, fallbackIndex = 0): string {
  const varName = kpiColorVarName(key);
  const value = varName ? cssVar(varName) : '';
  return value || KPI_FALLBACK_COLORS[fallbackIndex % KPI_FALLBACK_COLORS.length];
}

export function kpiColorDark(key: string | null | undefined, fallbackIndex = 0): string {
  const varName = kpiColorVarName(key, true);
  const value = varName ? cssVar(varName) : '';
  return value || kpiColor(key, fallbackIndex);
}
